import { Typography } from '@material-ui/core'
import pickBy from 'lodash/pickBy'
import range from 'lodash/range'
import React, { useRef } from 'react'
import { useSize } from 'react-hook-size'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { SmallButton } from '../../components'
import { getConcreteMonitorDevice } from '../../state/DeviceById/selectors'
import { getShowSurfaceTemp } from '../../state/SystemCache/selectors'
import { ConcreteLevel, Device, Log, Sensors, State } from '../../types'
import { tempLabel, timeLabelHmd } from '../../utils'
import CompTimeHeader from './components/CompTimeHeader'
import RegistanceTable from './components/RegistanceTable'
import ConcreteDeviceHeader from './ConcreteDeviceHeader'
import GraphBody from './GraphBody'
import LastTimeLabel from './LastTimeLabel'
import { useGraphConfigModal, useViewMode } from './useViewConfig'

const BASE_WIDTH = 1057 // CardContainerの基準幅 (px)
const ASPECT_RATIO_1ROW = 0.45 // 1段時のカード高さ/幅の比率
const ASPECT_RATIO_2ROW = 0.65 // 2段時のカード高さ/幅の比率
const REC_LABEL_HEIGHT = 36 // "●記録中" 行の推定高さ (px)
const BOTTOM_PADDING = 30 // カード下部の余白 (px)

// パネルのレベル表示確認用ハードコードサンプル。
// true の間は実データを上書きして MOCK_LEVELS の並びを CH1 から順に出す。
// CH の数が MOCK_LEVELS より多い場合、余りは level 0 (off)。
const MOCK_LEVEL_SAMPLE = false
const MOCK_LEVELS: ConcreteLevel[] = [0, 1, 2, 3, 7, 4, 5, 6]

function applyMockLevels(sensors: Sensors): Sensors {
  const entries = Object.entries(sensors)

  return entries.reduce<Sensors>((acc, [key, sensor], i) => {
    const level: ConcreteLevel = i < MOCK_LEVELS.length ? MOCK_LEVELS[i] : 0
    acc[Number(key)] = { ...sensor, level }

    return acc
  }, {})
}

function calcLayout(
  rowCount: 1 | 2,
): {
  aspectRatio: number
  sectionHeight: number
} {
  const aspectRatio = rowCount === 2 ? ASPECT_RATIO_2ROW : ASPECT_RATIO_1ROW
  const available = BASE_WIDTH * aspectRatio - REC_LABEL_HEIGHT - BOTTOM_PADDING
  const sectionHeight = Math.floor(rowCount === 2 ? available / 2 : available)

  return { aspectRatio, sectionHeight }
}

type Props = {
  deviceId: string
  colNum: number // 1, 2, 4
}

const CardContainerWrap: React.FC<{
  width: number
  aspectRatio: number
}> = props => {
  return (
    <div
      style={{
        overflow: 'hidden',
        height: `${(props.width || 0) * props.aspectRatio}px`,
      }}
    >
      {props.children}
    </div>
  )
}

const ConcreteDevice = (props: Props & { width: number }) => {
  const device = useSelector<State, Device | undefined>(state =>
    getConcreteMonitorDevice(state, props.deviceId),
  )

  const showSurfaceTemp = useSelector(getShowSurfaceTemp)
  const [viewMode] = useViewMode()
  const [, setGraphModalOpen] = useGraphConfigModal()

  if (!device) {
    return (
      <Typography variant="body1">Loading: {`${props.deviceId}`}</Typography>
    )
  }

  const rawLastLog =
    device.lastLogs[
      device.currentSessionId + (device.info.keepedNewSession ? 1 : 0)
    ]
  const lastLog =
    MOCK_LEVEL_SAMPLE && rawLastLog
      ? {
          ...rawLastLog,
          fields: {
            ...rawLastLog.fields,
            sensors: applyMockLevels(rawLastLog.fields.sensors),
          },
        }
      : rawLastLog

  const has22ch = lastLog
    ? Object.keys(lastLog.fields.sensors).some(k => Number(k) >= 11)
    : false
  const showDouble = has22ch
  const { aspectRatio, sectionHeight } = calcLayout(showDouble ? 2 : 1)

  // 淡水ログ判定: しきい値に levelD (resiD) があれば淡水許可。
  // 11ch / 22ch を問わず、淡水ログのときだけ水ラベルへ凡例を出す。
  const isFreshWater = device.info.threasholds
    ? Object.values(device.info.threasholds).some(
        t => t?.resistance.levelD != null,
      )
    : false

  if (device.info.status !== 'running') {
    return (
      <>
        <ConcreteDeviceHeader device={device} col={props.colNum} />
        <CardContainerWrap width={props.width} aspectRatio={aspectRatio}>
          <CardContainer
            col={props.colNum}
            width={props.width}
            aspectRatio={aspectRatio}
            data-temp={showSurfaceTemp}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div className="rec-label" data-stop="true">
                <Typography variant="body1" color="inherit">
                  ●記録停止中
                </Typography>
              </div>
            </div>
            <div />
          </CardContainer>
        </CardContainerWrap>
      </>
    )
  }

  /* NOTE: 擬似的に停止を表示する */
  if (device.info.keepExpired) {
    const { aspectRatio: keepExpiredAspectRatio } = calcLayout(1)
    return (
      <>
        <ConcreteDeviceHeader device={device} col={props.colNum} />
        <CardContainerWrap
          width={props.width}
          aspectRatio={keepExpiredAspectRatio}
        >
          <CardContainer
            col={props.colNum}
            width={props.width}
            aspectRatio={keepExpiredAspectRatio}
          >
            <CenterBox>
              <Typography>30分間レコーダと接続できなかったため、</Typography>
              <Typography>次の記録開始まで切断されます。</Typography>
            </CenterBox>
          </CardContainer>
        </CardContainerWrap>
      </>
    )
  }

  return (
    <>
      <ConcreteDeviceHeader device={device} col={props.colNum} />
      <CardContainerWrap width={props.width} aspectRatio={aspectRatio}>
        <CardContainer
          col={props.colNum}
          width={props.width}
          aspectRatio={aspectRatio}
          data-show-st={showSurfaceTemp}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className="rec-label">
              <Typography variant="body1" color="inherit">
                ●記録中
              </Typography>
            </div>

            <Typography variant="body1" style={{ color: 'white' }}>
              {lastLog ? (
                <>
                  <SmallButton
                    onClick={() => setGraphModalOpen(true)}
                    style={{
                      display: viewMode === 'table' ? 'none' : 'inline',
                    }}
                  >
                    グラフ設定
                  </SmallButton>
                  <LastTimeLabel timestamp={lastLog.timestamp} />
                </>
              ) : (
                'ログが足りません'
              )}
            </Typography>
          </div>
          {!lastLog && <div />}
          {lastLog &&
            (viewMode === 'table' ? (
              showDouble ? (
                <TableBodyDouble
                  lastLog={lastLog}
                  visibleSurface={showSurfaceTemp}
                  sectionHeight={sectionHeight}
                  isFreshWater={isFreshWater}
                />
              ) : (
                <TableBodySingle
                  lastLog={lastLog}
                  visibleSurface={showSurfaceTemp}
                  sectionHeight={sectionHeight}
                  isFreshWater={isFreshWater}
                />
              )
            ) : (
              <GraphBody device={device} />
            ))}
        </CardContainer>
      </CardContainerWrap>
    </>
  )
}

type SensorSectionProps = {
  sensors: Sensors
  compactionLabels: string[]
  fillingLabels: string[]
  surfaceTemps: string[]
  visibleSurface: boolean
  sectionHeight: number
  isFreshWater?: boolean
}

const SensorSection = ({
  sensors,
  compactionLabels,
  fillingLabels,
  surfaceTemps,
  visibleSurface,
  sectionHeight,
  isFreshWater,
}: SensorSectionProps) => (
  <div
    style={{
      height: `${sectionHeight}px`,
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <CompTimeHeader
      title="締固完了時刻"
      color="lime"
      labels={compactionLabels}
    />
    <CompTimeHeader title="充填完了時刻" color="aqua" labels={fillingLabels} />
    {visibleSurface && (
      <CompTimeHeader
        title="現在温度(℃)"
        color="orange"
        labels={surfaceTemps}
      />
    )}
    <div style={{ flex: 1, overflow: 'hidden' }}>
      <RegistanceTable sensors={sensors} isFreshWater={isFreshWater} />
    </div>
  </div>
)

type TableBodyProps = {
  lastLog: Log
  visibleSurface: boolean
  sectionHeight: number
  isFreshWater: boolean
}

// 段ごとの時刻・温度ラベルを ch オフセット付きで作る。
// compTimes は ch 1-22 の 1-index、変換後 sensors は ch1=0 始まりの配列。
// 上段は chOffset=0 (ch1-11)、下段は chOffset=11 (ch12-22)。
const buildSectionLabels = (lastLog: Log, chOffset: number) => {
  const compactionLabels = range(0, 12).map(i =>
    timeLabelHmd(lastLog.compTimes[i + chOffset]?.compactionEnd),
  )
  const fillingLabels = range(0, 12).map(i =>
    timeLabelHmd(lastLog.compTimes[i + chOffset]?.fillingEnd),
  )
  const surfaceTemps = [''].concat(
    range(1, 12).map(i => {
      const sensor = lastLog.fields.sensors[i - 1 + chOffset]

      return (sensor?.level ?? 0) === 0
        ? ''
        : tempLabel(sensor?.surfaceTemp, '')
    }),
  )

  return { compactionLabels, fillingLabels, surfaceTemps }
}

// React.memo: clock tick での wrapper 再レンダー時、props (lastLog は参照安定・他は
// プリミティブ) が不変なら重い表サブツリーの再レンダーをスキップする。
const TableBodySingle = React.memo(function TableBodySingle({
  lastLog,
  visibleSurface,
  sectionHeight,
  isFreshWater,
}: TableBodyProps) {
  const sensors = pickBy(
    lastLog.fields.sensors,
    (_v, k) => Number(k) < 11,
  ) as Sensors

  return (
    <SensorSection
      sensors={sensors}
      {...buildSectionLabels(lastLog, 0)}
      visibleSurface={visibleSurface}
      sectionHeight={sectionHeight}
      isFreshWater={isFreshWater}
    />
  )
})

const TableBodyDouble = React.memo(function TableBodyDouble({
  lastLog,
  visibleSurface,
  sectionHeight,
  isFreshWater,
}: TableBodyProps) {
  const upperSensors = pickBy(
    lastLog.fields.sensors,
    (_v, k) => Number(k) < 11,
  ) as Sensors
  const realLowerSensors = pickBy(
    lastLog.fields.sensors,
    (_v, k) => Number(k) >= 11,
  ) as Sensors
  const lowerSensors: Sensors =
    Object.keys(realLowerSensors).length > 0
      ? realLowerSensors
      : Object.entries(upperSensors).reduce<Sensors>((acc, [k, v]) => {
          acc[Number(k) + 11] = v
          return acc
        }, {})

  const commonProps = { visibleSurface, sectionHeight, isFreshWater }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <SensorSection
        sensors={upperSensors}
        {...buildSectionLabels(lastLog, 0)}
        {...commonProps}
      />
      <SensorSection
        sensors={lowerSensors}
        {...buildSectionLabels(lastLog, 11)}
        {...commonProps}
      />
    </div>
  )
})

const ResizeConcreteDevice = (props: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const { width } = useSize(ref)

  return (
    <div
      ref={ref}
      style={{
        display: 'grid',
        gridTemplateRows: 'max-content',
        marginBottom: '1.6rem',
      }}
    >
      <ConcreteDevice {...props} width={width || 0} />
    </div>
  )
}

export const CardContainer = styled.div<{
  col: number
  width: number
  aspectRatio?: number
}>`
  border: solid black 1px;
  border-radius: 4px;
  width: ${BASE_WIDTH}px;
  height: ${({ aspectRatio = ASPECT_RATIO_1ROW }) =>
    BASE_WIDTH * aspectRatio}px;
  min-width: 100px;
  padding: 4px;
  background: black;
  color: white;
  display: grid;
  grid-template-rows: max-content;
  transform: scale(${({ width }) => width / (BASE_WIDTH + 10)});
  transform-origin: top left;
  p {
    font-size: 22px !important;
  }
  .times .cell p {
    font-size: 18px !important;
  }

  .rec-label {
    background: white;
    color: red;
    padding: 4px;

    &[data-stop='true'] {
      color: black;
    }
  }
  .log-table {
    display: grid;
    grid-template-rows: 10% 10% 80%;
  }
  &[data-show-st='true'] {
    .log-table {
      grid-template-rows: 10% 10% 10% 70%;
    }
  }
`

const CenterBox = styled.div`
  display: grid;
  height: 460px;
  align-content: space-evenly;
  justify-content: center;
  text-align: center;
  vertical-align: middle;
  p {
    font-size: 40px !important;
  }
`

export default ResizeConcreteDevice
